import {
  ModDot,
  ModIconButton,
  ModSkeleton,
  ModTag,
  ModTooltip
} from '@moderneinc/design-system-components';
import {
  borderRadius,
  height,
  iconSize,
  semanticColors,
  spacing
} from '@moderneinc/design-system-tokens';
import { Box, Stack, Typography } from '@mui/material';
import type { FunctionComponent, ReactNode } from 'react';
import {
  useScmConnectionsLazyQuery,
  useScmConnectionsQuery
} from '../../__generated__/apollo-hooks';
import { DOCUMENTATION_SITE_SCM_SETUP_URL } from '../../constants/general';
import { accountSettingsUrl } from '../../helpers/link.helper';
import { useScmAuthorizationPopup } from '../../hooks/use-scm-authorization-popup.hook';
import { MinusIcon } from '../../icons/icons';
import { Link } from '../utilities/moderne-link/moderne-link.component';
import { ScmConnectionIcon } from '../utilities/scm-type-icon/scm-type-icon.component';

const MAX_VISIBLE_CONNECTIONS = 3;

export type ScmConnectionFields = {
  __typename: string;
  resourceId: string;
  isAuthorized: boolean;
};

const ScmIconWithDot: FunctionComponent<{
  configuration: ScmConnectionFields;
  variant?: 'contained' | 'default';
}> = ({ configuration, variant = 'default' }) => {
  const { isAuthorized } = configuration;

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-grid',
        placeItems: 'start'
      }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: spacing.spacing_4,
          height: spacing.spacing_4,
          borderRadius: borderRadius.full,
          ...(variant === 'contained' && {
            backgroundColor: semanticColors.color.surface.sunken,
            border: `1px solid ${semanticColors.color.border.primary}`
          })
        }}
      >
        <ScmConnectionIcon
          configuration={configuration}
          SvgIconProps={{ sx: { fontSize: iconSize.md } }}
        />
      </Box>
      <Box
        sx={{
          position: 'absolute',
          top: -9,
          right: -4
        }}
      >
        <ModDot
          size="large"
          variant="solid"
          color={isAuthorized ? 'success' : 'error'}
        >
          {isAuthorized ? null : <MinusIcon aria-hidden strokeWidth={5} />}
        </ModDot>
      </Box>
    </Box>
  );
};

export const ScmConnectionButton: FunctionComponent<{
  configuration: ScmConnectionFields;
  disableAction?: boolean;
  variant?: 'contained' | 'default';
}> = ({ configuration, disableAction = false, variant = 'default' }) => {
  const { isAuthorized, resourceId } = configuration;

  const [refetchScmConnections] = useScmConnectionsLazyQuery();
  const { authorize } = useScmAuthorizationPopup({
    onSuccess: refetchScmConnections
  });

  if (disableAction) {
    return <ScmIconWithDot configuration={configuration} variant={variant} />;
  }

  return (
    <ModTooltip
      title={
        isAuthorized
          ? `Connected to ${configuration.resourceId}`
          : `Authorize your current session with ${configuration.resourceId}`
      }
    >
      <ModIconButton
        data-testid="global-scm-connection-button"
        size="small"
        disabled={isAuthorized || disableAction}
        onClick={() => authorize(resourceId)}
      >
        <ScmIconWithDot configuration={configuration} variant={variant} />
      </ModIconButton>
    </ModTooltip>
  );
};

const ScmConnectionsRow: FunctionComponent<{ children: ReactNode }> = ({
  children
}) => (
  <Stack
    data-testid="scm-connections-row"
    direction="row"
    sx={{
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: `${spacing.spacing_3_4}px`,
      // The "No SCMs configured" caption is shorter than a slot and would
      // shrink the row under the rows below it (#8921).
      minHeight: `${height.button.small}px`
    }}
  >
    {children}
  </Stack>
);

export const ScmConnections: FunctionComponent = () => {
  const { data, loading } = useScmConnectionsQuery();

  const connections = data?.scmConnections;

  if (loading) {
    return (
      <ScmConnectionsRow>
        {Array.from({ length: MAX_VISIBLE_CONNECTIONS }, (_, index) => (
          <ModSkeleton
            key={`scm-skeleton-${index}`}
            data-testid="scm-connection-skeleton"
            width={height.button.small}
            height={height.button.small}
            sx={{ borderRadius: borderRadius.full }}
          />
        ))}
      </ScmConnectionsRow>
    );
  }

  if (!connections?.length) {
    return (
      <ScmConnectionsRow>
        <Typography variant="caption" color="primary">
          <Link href={DOCUMENTATION_SITE_SCM_SETUP_URL} external>
            No SCMs configured
          </Link>
        </Typography>
      </ScmConnectionsRow>
    );
  }

  const sortedConnections = [...connections].sort((a, b) => {
    if (a.isAuthorized !== b.isAuthorized) {
      return a.isAuthorized ? -1 : 1;
    }
    return a.resourceId?.localeCompare(b.resourceId) ?? 0;
  });

  const displayedConnections = sortedConnections.slice(
    0,
    MAX_VISIBLE_CONNECTIONS
  );
  const hasOverflow = connections.length > MAX_VISIBLE_CONNECTIONS;

  return (
    <ScmConnectionsRow>
      {displayedConnections.map((configuration, index) => (
        <ScmConnectionButton
          key={`scm-${configuration.resourceId}-${index}`}
          configuration={configuration}
          variant="contained"
        />
      ))}
      {hasOverflow && (
        <ModTag
          component={Link}
          href={accountSettingsUrl('connection')}
          label={`+${connections.length - MAX_VISIBLE_CONNECTIONS}`}
          size="small"
          shape="rounded"
          clickable
        />
      )}
    </ScmConnectionsRow>
  );
};
